'use client';

import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
    appendCatalogFilters,
    EMPTY_FILTERS,
    filtersKey,
    type FacetsResponse,
    type FiltersState,
} from '@/modules/shop/components/catalog/catalog-filter-utils';
import { SearchTracker } from '@/core/components/Analytics/EcommerceTrackers';

export interface CatalogContextType {
    filters: FiltersState;
    setFilters: (f: FiltersState | ((prev: FiltersState) => FiltersState)) => void;
    subcategories: Array<{ id: number; name: string; slug: string }>;
    productCount: number;
    setProductCount: (n: number) => void;
    currentPage: number;
    setCurrentPage: (p: number | ((prev: number) => number)) => void;
    sort: string;
    setSort: (s: string) => void;
    categoryId: number | null;
    facets: FacetsResponse | null;
    setFacets: (data: FacetsResponse | null) => void;
}

const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

function normalizedFilters(value: FiltersState | undefined, initialQuery: string): FiltersState {
    return value
        ? { ...EMPTY_FILTERS, ...value, facets: value.facets || {} }
        : { ...EMPTY_FILTERS, query: initialQuery };
}

export function CatalogProvider({
    children,
    subcategories = [],
    initialQuery = '',
    initialFilters,
    initialPage = 1,
    initialTotal = 0,
    // '' = no ?sort= on product queries → the shop's default listing order applies.
    initialSort = '',
    categoryId = null,
}: {
    children: React.ReactNode;
    subcategories?: Array<{ id: number; name: string; slug: string }>;
    initialQuery?: string;
    initialFilters?: FiltersState;
    initialPage?: number;
    initialTotal?: number;
    initialSort?: string;
    categoryId?: number | null;
}) {
    const [filters, setFilters] = useState<FiltersState>(() => normalizedFilters(initialFilters, initialQuery));
    const parent = useContext(CatalogContext);
    const [productCount, setProductCount] = useState<number>(initialTotal);
    const [currentPage, setCurrentPage] = useState<number>(initialPage);
    const [sort, setSort] = useState<string>(initialSort);
    const [facets, setFacets] = useState<FacetsResponse | null>(null);

    useEffect(() => {
        if (initialFilters) {
            setFilters(normalizedFilters(initialFilters, initialQuery));
        } else if (initialQuery) {
            setFilters(f => ({ ...f, query: initialQuery }));
        }
    }, [initialQuery, initialFilters]);

    useEffect(() => {
        if (typeof initialTotal === 'number' && initialTotal >= 0) {
            setProductCount(initialTotal);
        }
    }, [initialTotal]);

    useEffect(() => {
        if (typeof initialPage === 'number' && initialPage > 0) {
            setCurrentPage(initialPage);
        }
    }, [initialPage]);

    const currentFiltersKey = useMemo(() => filtersKey(filters), [filters]);
    const previousUrlState = useRef(`${currentFiltersKey}|${sort}`);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        const stateKey = `${currentFiltersKey}|${sort}`;
        if (previousUrlState.current !== stateKey) {
            previousUrlState.current = stateKey;
            url.searchParams.delete('strona');
            url.searchParams.delete('page');
            setCurrentPage(1);
        }
        appendCatalogFilters(url.searchParams, filters);
        // Any explicit choice goes to the URL — '' is the shop's default order (default_product_sort),
        // which may not be "newest", so "Najnowsze" has to survive a reload too.
        if (sort) url.searchParams.set('sort', sort);
        else url.searchParams.delete('sort');
        const next = `${url.pathname}${url.search}${url.hash}`;
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (next !== current) window.history.replaceState({}, '', next);
    }, [currentFiltersKey, filters, sort]);

    // Containers/rows render their children through another BlockRenderer that receives the
    // same `category`, so every nesting level used to open a fresh provider: a catalog-filters
    // block and the products grid sitting in different columns of a category template got
    // separate state — search/price filters never reached the grid and the count stayed at 0.
    // Only the outermost provider (the one the category page seeds) owns the state.
    if (parent) return <>{children}</>;

    return (
        <CatalogContext.Provider value={{
            filters,
            setFilters,
            subcategories,
            productCount,
            setProductCount,
            currentPage,
            setCurrentPage,
            sort,
            setSort,
            categoryId,
            facets,
            setFacets,
        }}>
            <SearchTracker query={filters.query} />
            {children}
        </CatalogContext.Provider>
    );
}

export function useCatalogContext() {
    return useContext(CatalogContext);
}
